import { RedoOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { CiLocationOn } from 'react-icons/ci';
import { MapContainer, Marker } from 'react-leaflet';
import { useHistory } from 'react-router-dom';

import AddressUnitsSelector from '../../../components/common/AddressUnitSelector';
import icon from '../../../components/map/constants';
/** Хүргэлт үүсгэх дэлгэц дэх хаягийн зурагтай ижил бүрэлдэхүүн, харагдац */
import { BaseLayers, MapCenterView, MapClickHandler } from '../../../components/map/mapParts';
import '../../../components/map/style.less';
import SectionCard from '../../../components/sectionCard/SectionCard';
import StepFooter from '../../../components/stepFooter/StepFooter';
import { imageUpload } from '../../../utils';
import { isHandledByInterceptor } from '../../../utils/apiError';
import { fetchAddressUnit } from '../../../utils/addressHelper';
import { addressStyleProps } from '../../../utils/format';
import { RegisterProviderContext } from '../RegisterProviderContext';

const { TextArea } = Input;
const { Text } = Typography;

const ADDRESS_URL = process.env.REACT_APP_ADDRESS_URL_V2;

/** Улаанбаатарын төв — байршил сонгоогүй үед газрын зургийн эхлэх цэг */
const DEFAULT_CENTER = { lat: 47.9123565, lon: 106.9151432 };

const DEFAULT_REGION_ID = '11';

/** Газрын зураг болон хайлтын хэсгийн өндөр */
const PANEL_HEIGHT = 320;

/** Форм 2 багана: жижиг дэлгэцэнд нэг багана болж хураагдана */
const COL_SPAN = { xs: 24, md: 12 };

const Step2 = ({ prevStep, setIsSuccessful }) => {
  const history = useHistory();

  const [addressArray, setAddressArray] = useState([]);
  const [merchantAddress, setMerchantAddress] = useState([]);

  const [addressMap, setAddressMap] = useState([]);
  const [aimagList, setAimagList] = useState([]);
  const [aimagLoading, setAimagLoading] = useState(false);
  const [regionId, setRegionId] = useState(DEFAULT_REGION_ID);
  const [aimagValue, setAimagValue] = useState(null);
  const [searchKey, setSearchKey] = useState('');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedAddress, setSelectedAddress] = useState();
  // Сонгосон хаяг биш, зөвхөн зургийн харагдац — дарсан цэг рүү зөөнө
  const [center, setCenter] = useState(DEFAULT_CENTER);
  // Leaflet-д контейнер хэмжээтэй болсны дараа mount хийнэ (эс бөгөөс сааралдана)
  const [ready, setReady] = useState(false);

  const { formStep1Values, formStep2Values, setFormStep2Values } =
    useContext(RegisterProviderContext) ?? {};

  useEffect(() => {
    setReady(true);
    // Эхлэхэд орчмын хаягуудыг харуулна — жагсаалт хоосон нээгдэхгүй
    getNearbyAddresses(DEFAULT_CENTER);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setAimagLoading(true);
    fetchAddressUnit()
      .then(({ docs }) => {
        const aimags = docs.map((d) => ({
          label: d?.name ?? d?._source?.name_full_mn ?? d?._source?.name_mn,
          value: d.name,
          id: d._id,
        }));
        setAimagList(aimags);

        const defaultAimag = aimags.find((a) => a.id === DEFAULT_REGION_ID);
        if (defaultAimag) {
          setAimagValue(defaultAimag.value);
          setRegionId(defaultAimag.id);
        }
      })
      .finally(() => setAimagLoading(false));
  }, []);

  const onHandleAddress = (value) => {
    setAddressArray(value);
    setMerchantAddress(value);
  };

  const fetchAddresses = (params) => {
    setSearching(true);
    axios
      .get(`${ADDRESS_URL}/search-nearby-address`, {
        params,
        headers: { 'X-Caller-Type': 'internal' },
      })
      .then(({ data = {} }) => {
        setAddressMap(data?.result?.length ? data.result : []);
      })
      .catch((err) => {
        console.error('search-nearby-address: ', err);
      })
      .finally(() => setSearching(false));
  };

  const getNearbyAddresses = ({ lat, lon }) => fetchAddresses({ lat, lon });

  /**
   * Газрын зураг дээр дарсан цэг — зөвхөн ойролцоох хаягуудыг гаргана.
   * Сонголтыг ХӨНДӨХГҮЙ: салбарын хаяг нь бүртгэлтэй хаяг байх ёстой тул
   * хэрэглэгч жагсаалтаас батлана.
   */
  const onMapClick = ({ lat, lng }) => {
    setCenter({ lat, lon: lng });
    getNearbyAddresses({ lat, lon: lng });
  };

  /** Жагсаалтаас хаяг сонгох — id-аар хайж бүтэн объектыг өгнө */
  const onSelectAddress = (addressId) => {
    const address = addressMap.find((item) => item.addressId === addressId);
    if (!address) return;

    setSelectedAddress(address);
    setFormStep2Values({
      address: {
        name: address.name,
        location: address.location,
        address: address.address,
        addressId: address.addressId,
      },
    });
    if (address?.location) setCenter(address.location);
  };

  const resetView = () => setCenter(selectedAddress?.location ?? DEFAULT_CENTER);

  const onSearch = () => {
    if (!aimagValue) {
      message.error('Хот/Аймаг сонгоно уу.');
      return;
    }
    if (!searchKey) {
      message.error('Хаяг оруулна уу.');
      return;
    }
    fetchAddresses({ searchAddressText: searchKey, regionId });
  };

  const onFinish = async (values) => {
    const params = { ...formStep1Values, ...values, branchAddress: merchantAddress };

    const attachment =
      formStep1Values.affiliation === 'C'
        ? [
            formStep1Values?.idCardFront[0]?.originFileObj,
            formStep1Values?.idCardBack[0]?.originFileObj,
          ]
        : [
            formStep1Values?.certificateFront[0]?.originFileObj,
            formStep1Values?.certificateBack?.[0]?.originFileObj,
          ].filter((el) => el);

    setSubmitting(true);
    try {
      const brandLogo = await imageUpload(
        formStep1Values.brandLogo[0].originFileObj,
        'merchantRequest'
      );
      const att1 = await imageUpload(attachment[0], 'merchantRequest');
      const att2 =
        attachment?.length > 1 ? await imageUpload(attachment[1], 'merchantRequest') : null;

      params.brandLogo = brandLogo;
      delete params.idCardBack;
      delete params.idCardFront;
      delete params.certificateBack;
      delete params.certificateFront;
      params.attachment = [att1, att2].filter((el) => el);
      params.branchAddressLocation = {
        ...formStep2Values?.address,
        address: formStep2Values?.address?.address?.map(({ _id, ...rest }) => rest),
      };

      const { data } = await axios.post('/api/v1.0/merchant', params);
      if (data.statusCode === 200) {
        setIsSuccessful(true);
        history.push('/');
        return;
      }

      // Interceptor мэдэгдээгүй кодод л локал мэдэгдэл гаргана — эс тэгвээс давхардана
      if (!isHandledByInterceptor(data)) {
        message.error(data?.message_mn ?? data?.message ?? 'Хүсэлт илгээхэд алдаа гарлаа.', 6);
      }
    } catch (error) {
      // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
      console.error('Merchant Request Create: ', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderResults = () => {
    if (!addressMap?.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Газрын зураг дээр дарж эсвэл хайлт хийж хаягаа сонгоно уу.
            </Text>
          }
          style={{ margin: 0, paddingTop: 48 }}
        />
      );
    }

    return (
      <Radio.Group
        value={selectedAddress?.addressId}
        onChange={(e) => onSelectAddress(e.target.value)}
        className="pp_map_panel__radios"
      >
        {addressMap.map((address) => (
          <Radio key={address.addressId} value={address.addressId}>
            <Space direction="vertical" size={0}>
              <Text>{address.name}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {address.nameFull ?? address.nameShort}
              </Text>
            </Space>
            {selectedAddress?.addressId === address.addressId && (
              <CiLocationOn className="pp_map_panel__check" size={20} />
            )}
          </Radio>
        ))}
      </Radio.Group>
    );
  };

  return (
    <Form autoComplete="off" layout="vertical" onFinish={onFinish} initialValues={{}}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <SectionCard icon={<CiLocationOn />} title="Салбарын мэдээлэл">
          <Row gutter={16}>
            <Col {...COL_SPAN}>
              <Form.Item
                label="Салбарын нэр"
                name="branchName"
                rules={[{ required: true, message: 'Салбарын нэр оруулна уу' }]}
              >
                <Input placeholder="Салбарын нэр" />
              </Form.Item>
            </Col>
            <Col {...COL_SPAN}>
              <Form.Item
                label="Салбарын утас"
                name="branchPhoneNumber"
                rules={[{ required: true, message: 'Салбарын утас оруулна уу' }]}
              >
                <Input placeholder="99999999" />
              </Form.Item>
            </Col>
          </Row>

          <AddressUnitsSelector
            selectedUnits={addressArray}
            onChange={onHandleAddress}
            styleProps={addressStyleProps}
          />

          <Row gutter={16}>
            {/* Урт текст тул мөрийг бүтнээр эзэлнэ */}
            <Col span={24}>
              <Form.Item
                label="Дэлгэрэнгүй хаяг"
                name="branchAddressDetail"
                style={{ marginBottom: 0 }}
              >
                <TextArea autoSize={{ minRows: 3, maxRows: 5 }} placeholder="Дэлгэрэнгүй хаяг" />
              </Form.Item>
            </Col>
          </Row>
        </SectionCard>

        <SectionCard icon={<CiLocationOn />} title="Салбарын байршил">
          <div className="pp_map_panel">
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
              {selectedAddress
                ? `Сонгосон байршил: ${selectedAddress.name}`
                : 'Газрын зураг дээр дарж эсвэл хайж, жагсаалтаас байршлаа сонгоно уу.'}
            </Text>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={14}>
                <div className="pp_map_panel__canvas" style={{ height: PANEL_HEIGHT }}>
                  {ready && (
                    <MapContainer
                      center={[center.lat, center.lon]}
                      zoom={16}
                      scrollWheelZoom={false}
                      doubleClickZoom={false}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <BaseLayers />
                      <MapClickHandler onClick={onMapClick} />
                      <Marker position={[center.lat, center.lon]} icon={icon} />
                      <MapCenterView center={center} />
                    </MapContainer>
                  )}

                  <Tooltip title="Сонгосон байршил руу буцах" placement="left">
                    <Button
                      className="pp_map_panel__reset"
                      icon={<RedoOutlined rotate={270} />}
                      onClick={resetView}
                    />
                  </Tooltip>
                </div>
              </Col>

              <Col xs={24} lg={10}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {/* Хайлтын хүрээ — хаягийн талбар биш тул Form.Item-гүй */}
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Хайх хот/аймаг
                    </Text>
                    <Select
                      placeholder="Сонгох"
                      options={aimagList}
                      loading={aimagLoading}
                      value={aimagValue || undefined}
                      showSearch
                      optionFilterProp="label"
                      style={{ width: '100%' }}
                      onChange={(value, option) => {
                        setAimagValue(value);
                        setRegionId(option.id);
                      }}
                    />
                  </div>

                  <Input.Search
                    placeholder="Байр, байгууламжийн нэрээр хайх"
                    enterButton="Хайх"
                    allowClear
                    loading={searching}
                    value={searchKey}
                    onChange={(e) => setSearchKey(e.target.value)}
                    onSearch={onSearch}
                  />

                  <div className="pp_map_panel__results" style={{ height: PANEL_HEIGHT - 104 }}>
                    {/* Хайлт/ойролцоох хаяг татаж байх үед хоосон төлөв андуурагдахгүй */}
                    <Spin spinning={searching}>{renderResults()}</Spin>
                  </div>
                </Space>
              </Col>
            </Row>
          </div>
        </SectionCard>

        <StepFooter onPrev={prevStep} submitText="Хүсэлт илгээх" loading={submitting} />
      </Space>
    </Form>
  );
};

export default Step2;
